"use client";
import React, { useEffect, useRef, useState } from "react";

import "../styles/Appreciationpg.css";
import Beams from "./Beams";
import Header from "./Header";
import Footer from "./Footer";
import appreciationGif from "../assets/certify.gif";

import appCert1 from "../assets/appcert1.png";
import appCert2 from "../assets/appcert2.png";
import appCert3 from "../assets/appcert3.png";
import appCert4 from "../assets/appcert4.png";
import appCert5 from "../assets/appcert5.png";

const Appreciationpg = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const images = [appCert1, appCert2, appCert3, appCert4, appCert5];
  // 6× repetition gives plenty of runway in both drag directions
  const track = [...images, ...images, ...images, ...images, ...images, ...images];

  const trackRef       = useRef(null);
  const animOffsetRef  = useRef(0);   // How far the auto-scroll has moved (px)
  const dragStartXRef  = useRef(0);   // Mouse/touch X when drag began
  const dragOffsetRef  = useRef(0);   // How far dragged since drag started (px)
  const isDraggingRef  = useRef(false);
  const rafRef         = useRef(null);
  const lastTimeRef    = useRef(null);

  // Speed: px per second for auto-scroll
  const SPEED = 80;

  // One "set" width (1/6 of total track) — we wrap within this range
  const getSetWidth = () => {
    if (!trackRef.current) return 0;
    return trackRef.current.scrollWidth / 6;
  };

  // Apply transform with modular wrapping to prevent runaway
  const applyTransform = (offset) => {
    const setWidth = getSetWidth();
    if (setWidth === 0) return offset;
    // Keep offset within [-2*setWidth, 0] so we always have cards on both sides
    let wrapped = offset % setWidth;
    if (wrapped > 0) wrapped -= setWidth;
    if (!trackRef.current) return wrapped;
    trackRef.current.style.transform = `translateX(${wrapped}px)`;
    return wrapped;
  };

  useEffect(() => {
    // Start animation offset at 0 (will scroll left = negative direction)
    animOffsetRef.current = 0;
    lastTimeRef.current = null;

    const tick = (timestamp) => {
      if (!isDraggingRef.current) {
        if (lastTimeRef.current === null) lastTimeRef.current = timestamp;
        const delta = timestamp - lastTimeRef.current;
        lastTimeRef.current = timestamp;

        // Auto-scroll moves left (negative)
        animOffsetRef.current -= (SPEED * delta) / 1000;
        animOffsetRef.current = applyTransform(animOffsetRef.current);
      } else {
        lastTimeRef.current = null; // Reset so no jump when drag ends
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  /* ---- MOUSE EVENTS ---- */
  const onMouseDown = (e) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragOffsetRef.current = 0;
    if (trackRef.current) {
      trackRef.current.style.cursor = "grabbing";
    }
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - dragStartXRef.current;
    dragOffsetRef.current = dx;
    // Apply live drag offset on top of where auto-scroll paused
    const live = animOffsetRef.current + dx;
    if (trackRef.current) {
      const setWidth = getSetWidth();
      let wrapped = live % setWidth;
      if (wrapped > 0) wrapped -= setWidth;
      trackRef.current.style.transform = `translateX(${wrapped}px)`;
    }
  };

  const onMouseUp = () => {
    if (!isDraggingRef.current) return;
    // Commit the drag delta into animOffsetRef so auto-scroll resumes from new position
    animOffsetRef.current += dragOffsetRef.current;
    animOffsetRef.current = applyTransform(animOffsetRef.current);
    isDraggingRef.current = false;
    dragOffsetRef.current = 0;
    if (trackRef.current) {
      trackRef.current.style.cursor = "grab";
    }
  };

  /* ---- TOUCH EVENTS ---- */
  const onTouchStart = (e) => {
    isDraggingRef.current = true;
    dragStartXRef.current = e.touches[0].clientX;
    dragOffsetRef.current = 0;
  };

  const onTouchMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.touches[0].clientX - dragStartXRef.current;
    dragOffsetRef.current = dx;
    const live = animOffsetRef.current + dx;
    if (trackRef.current) {
      const setWidth = getSetWidth();
      let wrapped = live % setWidth;
      if (wrapped > 0) wrapped -= setWidth;
      trackRef.current.style.transform = `translateX(${wrapped}px)`;
    }
  };

  const onTouchEnd = () => {
    if (!isDraggingRef.current) return;
    animOffsetRef.current += dragOffsetRef.current;
    animOffsetRef.current = applyTransform(animOffsetRef.current);
    isDraggingRef.current = false;
    dragOffsetRef.current = 0;
  };

  return (
    <>

      <div className="appreciation-page-wrapper">

        {/* --- TOP SECTION --- */}
      {/* --- TOP SECTION --- */}
<div className="appreciationpg-container">

  {/* Background Beams */}
  <div className="app-silk-bg">
    <Beams
      beamWidth={1.4}
      beamHeight={30}
      beamNumber={14}
      lightColor="#00CCFF"
      speed={20}
      noiseIntensity={0}
      scale={0.19}
      rotation={59}
    />
  </div>

  {/* Top Label */}
  <div className="app-hero-inner">

    <div className="app-hero-left">

      <h1 className="app-hero-title">
        Recognized for <br />
         Excellence &amp; Integrity
      </h1>

      <p className="app-hero-desc">
        Our commitment to delivering superior quality and safety-first
        solutions has earned us the trust and admiration of leading
        organizations across industries. Each letter of appreciation
        stands as a testament to our unwavering dedication.
      </p>

      {/* Divider line */}
      <div className="app-hero-divider" />

      {/* Stats Row */}
      <div className="app-hero-stats">
        <div className="app-stat-item">
          <span className="app-stat-number">100<span className="app-stat-plus">+</span></span>
          <span className="app-stat-label">Projects Completed</span>
        </div>
        <div className="app-stat-divider" />
        <div className="app-stat-item">
          <span className="app-stat-number">100<span className="app-stat-plus">+</span></span>
          <span className="app-stat-label">Clients Served</span>
        </div>
        <div className="app-stat-divider" />
        <div className="app-stat-item">
          <span className="app-stat-number">13<span className="app-stat-plus">+</span></span>
          <span className="app-stat-label">Years of Trust</span>
        </div>
      </div>
    </div>

    {/* Right — GIF + Badge */}
    <div className="app-hero-right">
      <div className="app-hero-img-frame">
        <img src={appreciationGif} alt="Appreciation" className="app-hero-gif" />
        {/* Floating badge */}
        <div className="app-hero-badge">
          <span className="app-badge-icon">✦</span>
          <span className="app-badge-text">Trusted Since 2013</span>
        </div>
      </div>
    </div>

  </div>
</div>
        {/* --- INFINITE MARQUEE WITH DRAG --- */}
        <div className="appreciation-slider-section">
          <h2 className="slider-main-title">Appreciation Certification</h2>

          <div className="marquee-viewport">
            <div
              className="marquee-track"
              ref={trackRef}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}   // Release if cursor leaves
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {track.map((img, index) => (
                <div className="app-cert-card" key={index}>
                  <div className="app-cert-img-wrapper">
                    <img
                      src={img}
                      alt={`Appreciation Letter ${(index % images.length) + 1}`}
                      draggable={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>


    </>
  );
};

export default Appreciationpg;